import type {
  AreaDto,
  CalendariDto,
  CoberturaDto,
  DepartamentDto,
  RotacioDto,
  TornDto,
} from '@/api/planificacio';

import { locale } from '@/i18n/format';
import type { Idioma } from '@/i18n';

const dos = (n: number) => String(n).padStart(2, '0');

/** Data curta "21 set." per a capçaleres i franges (zona local de l'usuari). */
export function dataCurta(d: Date | string, idioma: Idioma): string {
  const parts = new Intl.DateTimeFormat(locale(idioma), {
    day: '2-digit',
    month: 'short',
  }).formatToParts(new Date(d));
  const dia = parts.find((p) => p.type === 'day')?.value ?? '';
  const mes = (parts.find((p) => p.type === 'month')?.value ?? '').replace(/\.$/, '');
  return `${dia} ${mes}`;
}

/** Torns que se solapen amb algun altre de la llista de referència (ignorant-ne el mateix id). */
export function tornsSolapats(a: TornDto[], b: TornDto[]): TornDto[] {
  return a.filter((x) =>
    b.some(
      (y) =>
        y.id !== x.id &&
        new Date(x.inici).getTime() < new Date(y.fi).getTime() &&
        new Date(y.inici).getTime() < new Date(x.fi).getTime(),
    ),
  );
}

export function diaClau(d: Date): string {
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

export function deDiaClau(clau: string): Date {
  const [a, m, d] = clau.split('-').map(Number);
  return new Date(a ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function sumaDies(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export function dilluns(d: Date = new Date()): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return sumaDies(r, -((r.getDay() + 6) % 7));
}

export function avui(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function rangDies(desde: Date, n: number): Date[] {
  return Array.from({ length: n }, (_, i) => sumaDies(desde, i));
}

export function diaSetmana(d: Date): number {
  return (d.getDay() + 6) % 7;
}

export function cobreixDia(t: Pick<TornDto, 'inici' | 'fi'>, dia: Date): boolean {
  const migdia = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), 12).getTime();
  return new Date(t.inici).getTime() <= migdia && new Date(t.fi).getTime() > migdia;
}

export function aLocalInput(iso: string): string {
  const d = new Date(iso);
  return `${diaClau(d)}T${dos(d.getHours())}:${dos(d.getMinutes())}`;
}

export function deLocalInput(v: string): string {
  return new Date(v).toISOString();
}

export function nouTorn(
  calendariId: number,
  dia: Date,
  titularId: number,
  horaCanvi = '08:00',
): TornDto {
  const [h, m] = horaCanvi.split(':').map(Number);
  const inici = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate(), h ?? 8, m ?? 0);
  return {
    id: null,
    calendariId,
    titularId,
    substitutId: null,
    inici: inici.toISOString(),
    fi: sumaDies(inici, 1).toISOString(),
    origen: 'MANUAL',
    notes: null,
  };
}

export function aplicaAssignacio(
  torns: TornDto[],
  calendariId: number,
  dia: Date,
  personaId: number,
  horaCanvi?: string,
): TornDto[] {
  const i = torns.findIndex((t) => cobreixDia(t, dia));
  if (i < 0) return [...torns, nouTorn(calendariId, dia, personaId, horaCanvi)];
  const t = torns[i] as TornDto;
  if (t.titularId === personaId || t.substitutId === personaId) return torns;
  const copia = [...torns];
  copia[i] = { ...t, substitutId: personaId };
  return copia;
}

export function tornsModificats(original: TornDto[], esborrany: TornDto[]): TornDto[] {
  const perId = new Map(original.filter((t) => t.id != null).map((t) => [t.id, t]));
  return esborrany.filter((t) => {
    if (t.id == null) return true;
    const o = perId.get(t.id);
    return (
      !o ||
      o.titularId !== t.titularId ||
      (o.substitutId ?? null) !== (t.substitutId ?? null) ||
      o.inici !== t.inici ||
      o.fi !== t.fi ||
      (o.notes ?? '') !== (t.notes ?? '')
    );
  });
}

export function participants(torns: TornDto[], extra: number[] = []): number[] {
  const ids: number[] = [];
  const afegeix = (id: number | null | undefined) => {
    if (id != null && !ids.includes(id)) ids.push(id);
  };
  torns.forEach((t) => {
    afegeix(t.titularId);
    afegeix(t.substitutId);
  });
  extra.forEach(afegeix);
  return ids;
}

export function solapats(a: TornDto[], b: TornDto[]): number {
  return tornsSolapats(a, b).length;
}

export type EstatCalendari = 'ACTIU' | 'BUITS' | 'INSUFICIENT' | 'INACTIU' | 'REVISIO';

export function estatCalendari(c: CalendariDto, cob?: CoberturaDto | null): EstatCalendari {
  if (!c.actiu) return 'INACTIU';
  if (!cob) return 'REVISIO';
  if (cob.percentatge < 80) return 'INSUFICIENT';
  if (cob.forats.length > 0) return 'BUITS';
  return 'ACTIU';
}

export const BADGE_ESTAT_CALENDARI: Record<EstatCalendari, string> = {
  ACTIU: 'ok',
  BUITS: 'incident',
  INSUFICIENT: 'critic',
  INACTIU: 'gray',
  REVISIO: 'gray',
};

export type NivellCobertura = 'COBERT' | 'AJUSTAT' | 'SENSE';

export function nivellCobertura(percentatge: number): NivellCobertura {
  if (percentatge >= 100) return 'COBERT';
  if (percentatge > 0) return 'AJUSTAT';
  return 'SENSE';
}

export function nomAmbit(
  c: Pick<CalendariDto, 'departamentId' | 'areaId'>,
  departaments: DepartamentDto[],
  arees: AreaDto[],
): string {
  if (c.areaId != null) {
    const a = arees.find((x) => x.id === c.areaId);
    const d = a ? departaments.find((x) => x.id === a.departamentId) : undefined;
    if (!a) return `#${c.areaId}`;
    return d ? `${d.nom} · ${a.nom}` : a.nom;
  }
  if (c.departamentId != null) {
    return departaments.find((x) => x.id === c.departamentId)?.nom ?? `#${c.departamentId}`;
  }
  return '';
}

export function rotacioActiva(rotacions?: RotacioDto[] | null): RotacioDto | undefined {
  return rotacions?.find((r) => r.actiu) ?? rotacions?.[0];
}

export function csv(valors: (string | number | null | undefined)[]): string {
  return valors.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';');
}

export function descarregaCsv(nom: string, files: string[]): void {
  const blob = new Blob([`\uFEFF${files.join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
